import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { CompanyContactApiClient } from '../company-contact-api-client.service';
import { companyCreateContactEvents } from './company-create-contact.events';

/**
 * Signal store for the "create & attach contact to a company" workflow.
 *
 * Drives `POST /companies/{companyId}/create_contact`. Exposes the standard
 * `requestStatus` signals from `withRequestStatus()`.
 *
 * Not a CRUD entity store — this is a one-shot workflow (Pattern B) that ends
 * when the contact is created; the new contact is surfaced as the
 * `createContactSucceeded` event payload so downstream stores (e.g. a contact
 * list) can react without owning the attachment logic.
 *
 * @example
 * const dispatch = injectDispatch(companyCreateContactEvents);
 * dispatch.createContactRequested({ companyId: 3, contact: {...} });
 */
export const CompanyCreateContactStore = signalStore(
  { providedIn: 'root' },
  withRequestStatus(),
  withReducer(
    on(companyCreateContactEvents.createContactRequested, () => ({
      requestStatus: 'loading',
    })),
    on(companyCreateContactEvents.createContactSucceeded, () => ({
      requestStatus: 'success',
    })),
    on(companyCreateContactEvents.createContactFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    }))
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      companyContacts = inject(CompanyContactApiClient)
    ) => ({
      createContact$: events
        .on(companyCreateContactEvents.createContactRequested)
        .pipe(
          switchMap(({ payload }) =>
            companyContacts.create(payload.companyId, payload.contact).pipe(
              mapResponse({
                next: (contact) =>
                  companyCreateContactEvents.createContactSucceeded(contact),
                error: (error: HttpErrorResponse) =>
                  companyCreateContactEvents.createContactFailed({
                    error: resolveHttpError(error).code,
                  }),
              })
            )
          )
        ),
    })
  )
);
